import { useState } from 'react';

import { sendVouchers } from '../../api/vouchers';

type FormStep = 'sender' | 'recipients' | 'confirmation' | 'success';

export function useVoucherConfigLogic() {
  const [step, setStep] = useState<FormStep>('sender');
  const [senderInfo, setSenderInfo] = useState({
    name: '',
    email: '',
  });
  const [recipientEmails, setRecipientEmails] = useState<string[]>(
    Array(10).fill('')
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validateSenderInfo = () => {
    const newErrors: Record<string, string> = {};

    if (!senderInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!senderInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(senderInfo.email)) {
      newErrors.email = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRecipients = () => {
    const newErrors: Record<string, string> = {};
    const filledEmails = recipientEmails.filter((email) => email.trim() !== '');

    if (filledEmails.length === 0) {
      newErrors.recipients = 'At least one recipient email is required';
    } else {
      recipientEmails.forEach((email, index) => {
        if (email.trim() && !/\S+@\S+\.\S+/.test(email)) {
          newErrors[`recipient-${index}`] = 'Email is invalid';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (step === 'sender' && validateSenderInfo()) {
      setStep('recipients');
    } else if (step === 'recipients' && validateRecipients()) {
      setStep('confirmation');
    }
  };

  const handlePrevStep = () => {
    if (step === 'recipients') {
      setStep('sender');
    } else if (step === 'confirmation') {
      setStep('recipients');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Filter out empty recipient emails
      const validRecipients = recipientEmails.filter(
        (email) => email.trim() !== ''
      );

      await sendVouchers({
        senderName: senderInfo.name,
        senderEmail: senderInfo.email,
        recipientEmails: validRecipients,
      });

      setStep('success');
    } catch {
      setErrors({ submit: 'Failed to send vouchers. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecipientEmailChange = (index: number, value: string) => {
    const newEmails = [...recipientEmails];
    newEmails[index] = value;
    setRecipientEmails(newEmails);

    // Clear error for this field if it exists
    if (errors[`recipient-${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`recipient-${index}`];
      setErrors(newErrors);
    }
  };

  return {
    step,
    senderInfo,
    setSenderInfo,
    recipientEmails,
    setRecipientEmails,
    termsAccepted,
    setTermsAccepted,
    isSubmitting,
    errors,
    setErrors,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    handleRecipientEmailChange,
  };
}
